import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useStopwatch } from "react-timer-hook";

import { Comments } from "./Comments";
import { Exercise } from "components/Exercise";
import { PracticeFooter } from "./PracticeFooter";
import { ProgressBar } from "./ProgressBar";
import { useAuth } from "hooks/useAuth";
import { useCompare } from "hooks/usePrevious";
import { useWindowFocus } from "hooks/useWindowFocus";
import { useNextExercise } from "hooks/useNextExercise";
import "./practice.scss";

const LENGTH = 5 * 60 * 1000;
const MAX_QUESTIONS = 10;

function Practice(props) {
  const history = useHistory();
  const { glickoHistory } = useAuth();
  const [exerciseData, setExerciseData] = useState(null);
  const nextExercise = useNextExercise();
  const [answer, setAnswer] = useState(null);
  const [startTime] = useState(new Date());
  const [progress, setProgress] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const { current: audioRight } = useRef(new Audio("/right.mp3"));
  const { current: audioWrong } = useRef(new Audio("/wrong.mp3"));
  const [startTimestamp, setStartTimestamp] = useState(new Date());
  const stopwatch = useStopwatch();

  const exercise = exerciseData ? exerciseData.exercise : null;
  const isCorrect = exercise && answer === exercise.answer;

  const commentsRef = useRef();
  const scrollContainerRef = useRef();

  const exerciseId = exercise ? exercise.id : null;
  const exerciseMockGlicko = useMemo(
    () =>
      exerciseId === null
        ? null
        : Math.round(
            (glickoHistory.length > 0 ? glickoHistory.slice(-1)[0] : 1500) +
              Math.random() * 200 -
              100
          ),
    [exerciseId, glickoHistory]
  );

  const exerciseChanged = useCompare(exercise);

  useEffect(() => {
    if (exerciseChanged) {
      stopwatch.reset();
      stopwatch.start();
      setStartTimestamp(new Date());
    }
  }, [exerciseChanged, stopwatch]);

  const focus = useWindowFocus();
  useEffect(() => {
    if (focus) {
      !answer && stopwatch.start();
    } else {
      stopwatch.pause();
    }
  }, [focus, answer, stopwatch]);

  useEffect(() => {
    if (exerciseData == null && !nextExercise.loading) {
      setExerciseData(nextExercise.exerciseData);
    }
  }, [nextExercise, exerciseData]);

  useEffect(() => {
    if (commentsRef.current) {
      scrollContainerRef.current.scrollTo({
        top:
          commentsRef.current.offsetTop -
          scrollContainerRef.current.offsetHeight +
          280,
        behavior: "smooth"
      });
    }
  }, [answer]);

  useEffect(() => {
    if (exerciseData && exerciseData.exercise) {
      window.analytics.track("exercise.displayed", {
        id: exerciseData.exercise.id
      });
    }
  }, [exerciseData]);

  if (!exercise) return <>Loading...</>;

  const glicko = glickoHistory.slice(-1)[0];
  const prevGlicko =
    !nextExercise.loading && glickoHistory.length > 1 && answer
      ? glickoHistory.slice(-2)[0]
      : null;

  return (
    <div className={`grid`}>
      <div className={`progressBar`}>
        <ProgressBar
          onClose={exit}
          progress={Math.min(progress / LENGTH, 1)}
          time={stopwatch}
        />
      </div>

      <main
        ref={scrollContainerRef}
        className={`practice-main ${
          answer && !nextExercise.loading && "footer-visible"
        }`}
      >
        <div className="practice-main-exercise-container">
          <Exercise
            exercise={Object.assign(exercise, {
              glicko: exerciseMockGlicko
            })}
            answer={answer}
            onAnswer={handleAnswer}
          />
        </div>
        {
          <Comments
            show={Boolean(answer)}
            ref={commentsRef}
            exerciseId={exercise.id}
          />
        }
      </main>
      <div className={`footer`}>
        {answer && (
          <PracticeFooter
            isCorrect={isCorrect}
            onClickContinue={handleNext}
            exerciseId={exercise.id}
            glicko={glicko}
            prevGlicko={prevGlicko}
          />
        )}
      </div>
    </div>
  );

  function handleAnswer(answer) {
    const isCorrect = answer === exercise.answer;
    (isCorrect ? audioRight : audioWrong).play();
    stopwatch.pause();
    setAnswer(answer);
    const { seconds, minutes, hours } = stopwatch;
    const responseTime = seconds + minutes * 60 + hours * 3600;
    window.analytics.track("exercise.answered", {
      answer,
      id: exerciseData.exercise.id,
      isCorrect,
      responseTime,
      startTimestamp
    });
    setProgress(
      Math.max(
        // +1 because the user just answered a question
        ((1 + questionsAnswered) * LENGTH) / MAX_QUESTIONS,
        new Date() - startTime
      )
    );
    nextExercise.submitAnswer({ answer, responseTime, startTimestamp });
  }

  function exit() {
    window.analytics.track("session.closed");
    const answered = questionsAnswered + Number(Boolean(answer));
    if (answered > 0) {
      history.push({
        pathname: `/summary/${answered}`
      });
    } else {
      history.push(`/profile`);
    }
  }

  function handleNext() {
    if (progress >= LENGTH) {
      history.push({
        pathname: `/summary/${questionsAnswered + 1}`
      });
    } else {
      // XXX: is this even needed?
      if (!nextExercise.loading) {
        setExerciseData(nextExercise.exerciseData);
        scrollContainerRef.current.scrollTo({ top: 0 });
      } else {
        setExerciseData(null);
      }
      setAnswer(null);
      setQuestionsAnswered(x => x + 1);
    }
  }
}

export { Practice };
